import { useCallback, useEffect, useState } from 'react';

// Hooks
import useFetchIntervalData from '../../../../../packages/hooks/useFetchIntervalData';
import useSubscriptionClick from '../../../../../packages/hooks/useSubscriptionClick';

// Types
import { IPlayerConfig, IPlayerResponse, SDKOptions } from '../../../../../packages/types';
import { IGeneralSubscriberInfo } from '../../../../../packages/constants/subscribeTypes';

import { getPlayerStatistic } from '../../../../../packages/services/football/player';
import { normalizePlayerStatistics } from '../../normalizers/player/normalizePlayerStatisticsResponseData';

interface IUsePlayerStatisticsArgs extends IGeneralSubscriberInfo {
  dataRefreshTime?: IPlayerConfig['dataRefreshTime'];
  dataPlayer?: IPlayerConfig['dataPlayer'];
  dataSeason?: IPlayerConfig['dataSeason'];
  sdkOptions: SDKOptions;
  scheme?: string[];
  dataLabels?: IPlayerConfig['dataLabels'];
  dataEntityLinks?: IPlayerConfig['dataEntityLinks'];
}

export default function usePlayerStatistics({
  dataRefreshTime,
  dataWidgetId,
  dataWidgetSport,
  subscribers,
  dataWidgetCuid,
  dataPlayer,
  dataSeason,
  sdkOptions,
  scheme,
  dataLabels,
}: IUsePlayerStatisticsArgs) {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<IPlayerResponse[]>();
  const [seasonOptions, setSeasonsOptions] = useState<{ label: string; value: string }[]>();
  const [rowOptions, setRowOptions] = useState<{ name: string; value: string }[]>();

  const fetchPlayerStatistics = useCallback(
    (manageLoader: boolean) => {
      manageLoader && setLoading(true);
      if (dataPlayer) {
        getPlayerStatistic({ player_ids: dataPlayer, sdkOptions })
          .then(({ statistics }) => {
            manageLoader && setLoading(false);
            statistics && setStatistics(statistics);
          })
          .catch((error: unknown) => {
            console.error(error);
          });
      }
    },
    [dataPlayer],
  );

  useFetchIntervalData(fetchPlayerStatistics, dataRefreshTime);

  useEffect(() => {
    fetchPlayerStatistics(true);
  }, [fetchPlayerStatistics]);

  useEffect(() => {
    const { seasons, rows } = normalizePlayerStatistics({
      statistics,
      season: dataSeason,
      scheme,
      dataLabels,
    });

    setSeasonsOptions(seasons);
    setRowOptions(rows);
  }, [dataSeason, statistics, scheme, dataLabels]);

  return {
    showWidget: true,
    widgetData: { seasons: seasonOptions ?? [], rows: rowOptions, isLoading },
    handleClick: useSubscriptionClick({ dataWidgetId, dataWidgetSport, dataWidgetCuid, subscribers }),
  };
}
